import React, { useEffect, useId, useRef } from 'react';
import styles from './Modal.module.css';

const SIZE_CLASS = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
  xl: styles.sizeXl,
  xxl: styles.sizeXxl
};

export default function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  /** When true, backdrop click does not close the dialog. */
  closeOnBackdrop = true,
  closeOnEscape = true,
  showClose = true,
  /** Block the X control (e.g. during a critical submit). */
  closeDisabled = false,
  /** When true, body is a flex column with hidden overflow so children can fill height (e.g. split panes). */
  bodyFill = false,
  /** Extra class on the scrollable body (e.g. tinted import shell). */
  bodyClassName = '',
  /** Cream shell + soft dim overlay (import-style dialogs). */
  warmTone = false,
  /** Stacking order for nested modals (default matches .overlay in Modal.module.css). */
  zIndex = 2000,
  children
}) {
  const overlayRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !closeOnEscape || !onClose || closeDisabled) return undefined;
    const handler = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, closeOnEscape, onClose, closeDisabled]);

  if (!open) return null;

  const handleOverlayMouseDown = (event) => {
    if (!closeOnBackdrop || !onClose || closeDisabled) return;
    if (event.target === overlayRef.current) onClose();
  };

  const overlayClass = [styles.overlay, warmTone && styles.overlayWarm].filter(Boolean).join(' ');
  const contentClass = [styles.content, SIZE_CLASS[size] || SIZE_CLASS.md, warmTone && styles.contentWarm]
    .filter(Boolean)
    .join(' ');
  const headerClass = [styles.header, warmTone && styles.headerWarm].filter(Boolean).join(' ');
  const bodyClass = [styles.body, bodyFill ? styles.bodyFill : '', warmTone && styles.bodyWarm, bodyClassName]
    .filter(Boolean)
    .join(' ');
  const footerClass = [styles.footer, warmTone && styles.footerWarm].filter(Boolean).join(' ');

  return (
    <div
      className={overlayClass}
      ref={overlayRef}
      style={{ zIndex }}
      onMouseDown={handleOverlayMouseDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
    >
      <div className={contentClass}>
        {(title || description || showClose) && (
          <header className={headerClass}>
            <div className={styles.headerText}>
              {title ? (
                <h2 id={titleId} className={styles.title}>
                  {title}
                </h2>
              ) : null}
              {description ? <p className={styles.description}>{description}</p> : null}
            </div>
            {showClose && onClose ? (
              <button
                type="button"
                onClick={onClose}
                className={styles.closeButton}
                aria-label="Close dialog"
                disabled={closeDisabled}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            ) : null}
          </header>
        )}

        <div className={bodyClass}>
          {children}
        </div>

        {footer ? <footer className={footerClass}>{footer}</footer> : null}
      </div>
    </div>
  );
}
